// src/utils/routes.js
//
// Чистый модуль: превращает состояние навигации (view / открытая
// задача / проект / файл / папка + активная вкладка) в хэш-фрагмент
// URL и обратно. Единственное место, знающее форму ссылки. Ни React,
// ни DOM, ни домен задач здесь нет - только строки.
//
// Формат (hash-роутинг - не требует rewrite-правил на хостинге):
//   #/view/<viewId>
//   #/task/<taskId>/<tab>
//   #/project/<projectId>/<tab>
//   #/file/<fileId>
//   #/folder/<folderId>
//
// FILE и FOLDER - «ссылки-точки входа»: у них нет собственной вкладки,
// они открывают владельца на вкладке «Вложения» и подсвечивают
// соответствующий узел. Форма ссылки - плоская, без вкладки.

export const ROUTE = Object.freeze({
  VIEW: "view",
  TASK: "task",
  PROJECT: "project",
  FILE: "file",
  FOLDER: "folder",
});

// Вкладка по умолчанию - используется, когда в фрагменте её нет,
// и когда указанная вкладка не входит в белый список.
export const DEFAULT_TAB = Object.freeze({
  [ROUTE.TASK]: "form",
  [ROUTE.PROJECT]: "info",
});

// Белый список вкладок. Фрагмент вроде "#/task/t1/admin" не должен
// протаскивать произвольную строку в приложение - неизвестная вкладка
// схлопывается к значению по умолчанию.
export const TABS = Object.freeze({
  [ROUTE.TASK]: Object.freeze([
    "form",
    "time",
    "subtasks",
    "chat",
    "files",
    "hist",
    "notes",
  ]),
  [ROUTE.PROJECT]: Object.freeze(["info", "tasks", "chat", "files", "hist"]),
});

// Белый список экранов, доступных из URL.
//
// ВАЖНО: этот список - не «пункты бокового меню» (для них есть navItems
// в MainLayout), а множество view, для которых существует case в
// MainLayout.renderView(). Оба конца роутинга - parseRoute здесь и
// route-эффект в MainLayout - обязаны опираться на этот список, а не
// на список кнопок меню.
//
// Разделы «Черновики» и «Шаблоны» объединены в один экран «Шаблоны
// и черновики»: старые id drafts и templates заменены на library.
// Старые ссылки обрабатываются через LEGACY_VIEW_REDIRECTS ниже.
export const VIEWS = Object.freeze([
  "tasks",
  "library",
  "gantt",
  "calendar",
  "projects",
  "staff",
  "reports",
  "archive",
  "requests",
  "journal",
  "cabinet",
]);

/**
 * Алиасы старых маршрутов, заменённых на новый раздел.
 *
 * Хранятся отдельно от VIEWS: старые id не должны попадать ни в
 * navItems, ни в buildRoute - иначе можно случайно сгенерировать
 * ссылку вида #/view/drafts. Но parseRoute обязан их распознавать,
 * чтобы не потерять существующие ссылки и закладки: он возвращает
 * маршрут на новый раздел с пометкой legacyId, а MainLayout по этой
 * пометке переписывает URL на актуальный.
 *
 * Если заказчик решит считать старые ссылки неизвестными - достаточно
 * опустошить объект: parseRoute начнёт возвращать null, и поведение
 * станет прежним («устаревшая ссылка ничего не делает»).
 */
export const LEGACY_VIEW_REDIRECTS = Object.freeze({
  drafts: "library",
  templates: "library",
});

// uid() выдаёт id из этих символов. Проверяем форму при разборе -
// правка адресной строки не должна подмешивать в дальнейшие
// потребители разделители и пробелы.
const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

const isKnownView = (v) => VIEWS.includes(v);
const isKnownTab = (kind, t) => (TABS[kind] || []).includes(t);

/**
 * Собрать URL-фрагмент из дескриптора. Возвращает '' для невалидного
 * ввода - вызывающий код использует это как «нечего писать»,
 * а не как «пиши мусор в адресную строку».
 */
export function buildRoute(route) {
  if (!route) return "";
  const { kind, id, tab } = route;

  if (kind === ROUTE.VIEW) {
    return isKnownView(id) ? `#/view/${id}` : "";
  }
  // FILE и FOLDER - плоские ссылки без вкладки. Тот же ID_RE, что и
  // у сущностей: подмена id на мусор не должна создавать ссылку.
  if (kind === ROUTE.FILE || kind === ROUTE.FOLDER) {
    return ID_RE.test(String(id || "")) ? `#/${kind}/${id}` : "";
  }
  if (kind === ROUTE.TASK || kind === ROUTE.PROJECT) {
    if (!ID_RE.test(String(id || ""))) return "";
    const safeTab = isKnownTab(kind, tab) ? tab : DEFAULT_TAB[kind];
    return `#/${kind}/${id}/${safeTab}`;
  }
  return "";
}

/**
 * Разобрать фрагмент в дескриптор маршрута. Возвращает null, если это
 * не известный маршрут - вызывающий код трактует это как «ничего
 * не делать», а не как «неизвестный маршрут», чтобы устаревшая ссылка
 * не переносила пользователя в неожиданное место.
 *
 * Для legacy-алиасов возвращает маршрут на новый раздел с
 * дополнительным полем legacyId: MainLayout по нему понимает, что
 * URL надо переписать на актуальный, и делает это до того, как
 * эффект синхронизации вида начнёт отрабатывать новое состояние.
 */
export function parseRoute(hash) {
  const raw = String(hash || "").replace(/^#\/?/, "");
  if (!raw) return null;

  const [head, second, third] = raw.split("/");

  if (head === ROUTE.VIEW) {
    if (isKnownView(second)) return { kind: ROUTE.VIEW, id: second };
    const redirect = LEGACY_VIEW_REDIRECTS[second];
    if (redirect) {
      return { kind: ROUTE.VIEW, id: redirect, legacyId: second };
    }
    return null;
  }
  if (head === ROUTE.FILE || head === ROUTE.FOLDER) {
    return ID_RE.test(String(second || "")) ? { kind: head, id: second } : null;
  }
  if (head === ROUTE.TASK || head === ROUTE.PROJECT) {
    if (!ID_RE.test(String(second || ""))) return null;
    const tab = isKnownTab(head, third) ? third : DEFAULT_TAB[head];
    return { kind: head, id: second, tab };
  }
  return null;
}

/** Один и тот же объект (view / task / project) - по виду и id. */
export function isSameEntity(a, b) {
  return !!a && !!b && a.kind === b.kind && a.id === b.id;
}
